import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, UserPlus } from 'lucide-react';
import type { Customer } from '@ach/shared';

import { api, ApiError } from '../lib/api.js';
import { Spinner, ErrorNote, EmptyState } from './components.js';

export function Customers() {
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    // Debounced: the trigram indexes make the query fast, but a request per
    // keystroke is still a request per keystroke.
    const timer = setTimeout(() => {
      setError(null);
      api
        .get<{ customers: Customer[] }>(
          `/api/admin/customers${search ? `?q=${encodeURIComponent(search)}` : ''}`,
          { signal: controller.signal },
        )
        .then((data) => setCustomers(data.customers))
        .catch((e) => {
          if (controller.signal.aborted) return;
          setError(e instanceof ApiError ? e.message : 'Could not load customers.');
        });
    }, 200);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [search]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Customers</h1>
        <Link to="/admin/customers/new" className="btn-primary ml-auto">
          <UserPlus className="h-4 w-4" aria-hidden />
          New customer
        </Link>
      </div>

      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden
        />
        <input
          className="field-input pl-9"
          placeholder="Search by name, company or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {customers === null ? (
        <Spinner label="Loading customers" />
      ) : customers.length === 0 ? (
        <EmptyState title={search ? 'No customers matched' : 'No customers yet'}>
          {search ? 'Try a different search.' : 'Add one to send your first payment request.'}
        </EmptyState>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Company</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Phone</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/admin/customers/${customer.id}`}
                      className="font-medium text-gold-800 hover:underline"
                    >
                      {customer.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{customer.company ?? '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600">{customer.email}</td>
                  <td className="px-4 py-2.5 text-slate-600">{customer.phone ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
